import { Check, Add } from '@mui/icons-material';
import { Stack, Chip } from '@mui/material';
import { withChildrenSlideAnimation } from '@/components/shared/animation';

export interface MultiSelectChipsProps<T = string> {
  options: {
    label: string;
    value: T;
  }[];
  currentValues?: T[] | null;
  onToggle: (value: T) => void;
  getValueKey?: (value: T) => string | number;
  shouldAnimate?: boolean;
  direction?: 'row' | 'column';
}

const AnimatedStack = withChildrenSlideAnimation(Stack);

export const MultiSelectChips = <T = string,>({
  options,
  currentValues,
  onToggle,
  getValueKey,
  shouldAnimate = true,
  direction = 'column',
}: MultiSelectChipsProps<T>) => {
  const chips = options.map(({ label, value: optionValue }) => {
    const isChecked = getValueKey
      ? (currentValues?.some(
          (currentValue) =>
            getValueKey(currentValue) === getValueKey(optionValue)
        ) ?? false)
      : (currentValues?.includes(optionValue) ?? false);

    return (
      <Chip
        key={getValueKey ? getValueKey(optionValue) : String(optionValue)}
        label={label}
        variant={isChecked ? 'filled' : 'outlined'}
        icon={isChecked ? <Check /> : <Add />}
        onClick={() => onToggle(optionValue)}
      />
    );
  });

  return shouldAnimate ? (
    <AnimatedStack
      gap={1}
      alignItems="flex-start"
      animationStepDelay={100}
      flexWrap="wrap"
      flexDirection={direction}
    >
      {chips}
    </AnimatedStack>
  ) : (
    <Stack
      gap={1}
      alignItems="flex-start"
      flexWrap="wrap"
      flexDirection={direction}
    >
      {chips}
    </Stack>
  );
};
